import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { Toaster } from "sonner";
import Lenis from "lenis";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Experience from "@/pages/Experience";
import Programme from "@/pages/Programme";
import PitchYatra from "@/pages/PitchYatra";
import Exhibition from "@/pages/Exhibition";
import InvestorAdda from "@/pages/InvestorAdda";
import KnowledgeLabs from "@/pages/KnowledgeLabs";
import Awards from "@/pages/Awards";
import Zones from "@/pages/Zones";
import Register from "@/pages/Register";
import Partner from "@/pages/Partner";
import Venue from "@/pages/Venue";
import Insights from "@/pages/Insights";
import Faq from "@/pages/Faq";
import Contact from "@/pages/Contact";
import LegalPage from "@/pages/LegalPage";

const ScrollManager = () => {
    const { pathname, hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const t = setTimeout(() => {
                const el = document.querySelector(hash);
                if (!el) return;
                if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
                else el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 150);
            return () => clearTimeout(t);
        }
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [pathname, hash]);
    return null;
};

const ScrollProgress = () => {
    const [w, setW] = useState(0);
    useEffect(() => {
        const fn = () => {
            const el = document.documentElement;
            const max = el.scrollHeight - el.clientHeight;
            setW(max > 0 ? (el.scrollTop / max) * 100 : 0);
        };
        fn();
        window.addEventListener("scroll", fn, { passive: true });
        return () => window.removeEventListener("scroll", fn);
    }, []);
    return (
        <div
            className="fixed top-0 left-0 h-[2px] z-[95] transition-[width] duration-150"
            style={{ width: `${w}%`, background: "linear-gradient(90deg, var(--wine), var(--gold), var(--champagne))" }}
            aria-hidden="true"
        />
    );
};

const AmbientGlow = () => (
    <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="orb orb-1 w-[38rem] h-[38rem] -top-40 -left-32" style={{ background: "rgba(165,28,48,0.13)" }} />
        <div className="orb orb-2 w-[30rem] h-[30rem] top-[42%] -right-36" style={{ background: "rgba(201,162,75,0.07)" }} />
        <div className="orb orb-1 w-[28rem] h-[28rem] -bottom-32 left-[28%]" style={{ background: "rgba(92,16,28,0.17)", animationDuration: "22s" }} />
    </div>
);

const MobileCta = () => (
    <div className="fixed bottom-0 inset-x-0 z-[70] sm:hidden">
        <Link to="/register" data-testid="sticky-register-btn" className="btn btn-gold w-full !py-4">
            Register Now
        </Link>
    </div>
);

function App() {
    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const lenis = new Lenis({ duration: 1.18, smoothWheel: true });
        window.__lenis = lenis;
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);

    return (
        <div className="App bg-[var(--bg-0)] min-h-screen">
            <BrowserRouter>
                <AmbientGlow />
                <ScrollManager />
                <ScrollProgress />
                <Navbar />
                <main className="relative">
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/experience" element={<Experience />} />
                        <Route path="/programme" element={<Programme />} />
                        <Route path="/pitchyatra" element={<PitchYatra />} />
                        <Route path="/exhibition" element={<Exhibition />} />
                        <Route path="/investor-adda" element={<InvestorAdda />} />
                        <Route path="/knowledge-labs" element={<KnowledgeLabs />} />
                        <Route path="/awards" element={<Awards />} />
                        <Route path="/special-zones" element={<Zones />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/partner" element={<Partner />} />
                        <Route path="/venue" element={<Venue />} />
                        <Route path="/insights" element={<Insights />} />
                        <Route path="/faq" element={<Faq />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/privacy" element={<LegalPage kind="privacy" />} />
                        <Route path="/terms" element={<LegalPage kind="terms" />} />
                        <Route path="/cookies" element={<LegalPage kind="cookies" />} />
                        <Route path="*" element={<Home />} />
                    </Routes>
                </main>
                <Footer />
                <MobileCta />
                <Toaster
                    theme="dark"
                    position="top-center"
                    toastOptions={{
                        style: {
                            background: "#2A0A12",
                            border: "1px solid rgba(201,162,75,0.35)",
                            color: "#F3EAD8",
                            borderRadius: "2px",
                        },
                    }}
                />
            </BrowserRouter>
        </div>
    );
}

export default App;
